<template>
    <div class="md:ppv2-w-[80%] ppv2-w-full">
        <div class="section-block mb-6">
            <div v-if="localOutils.length === 0" class="ppv2-text-center ppv2-py-12 ppv2-text-gray-500">
                <Wrench :size="48" class="ppv2-mb-4 ppv2-opacity-30 ppv2-inline-block" />
                <p>{{ $t('website_settings.outils.empty') }}</p>
            </div>

            <div v-for="(outil, index) in localOutils" :key="outil.id" class="ppv2-relative">
                <div class="ppv2-rounded-xl ppv2-p-6 ppv2-transition-all ppv2-border ppv2-border-black ppv2-border-opacity-60">
                    <div class="ppv2-flex ppv2-flex-col lg:ppv2-flex-row ppv2-items-center ppv2-gap-6">
                        <!-- Logo -->
                        <div class="lg:ppv2-w-1/4 ppv2-flex ppv2-justify-center">
                            <img
                                :src="outil.icon"
                                :alt="outil.name"
                                class="ppv2-max-h-20 ppv2-object-contain ppv2-bg-white p-1 ppv2-rounded-lg"
                            />
                        </div>

                        <!-- Content -->
                        <div class="lg:ppv2-w-3/4">
                            <h3 class="poppinsSemiBold ppv2-text-black ppv2-mb-2" :style="{ fontSize: normalizeFontSize(18) }">
                                {{ outil.name }}
                            </h3>
                            <p
                                class="ppv2-text-black ppv2-mb-4 ppv2-leading-relaxed"
                                :style="{ fontSize: normalizeFontSize(14) }"
                            >
                                {{ outil.description }}
                            </p>

                            <div class="ppv2-flex ppv2-flex-wrap" :style="{ gap: normalizeSize(16, true) }">
                                <BaseButton type="button" variant="primary" @click="openIntegrationModal(outil)">
                                    {{ $t('website_settings.outils.integrate') }}
                                </BaseButton>
                                <a
                                    :href="outil.url"
                                    target="_blank"
                                    class="ppv2-px-5 ppv2-py-2 ppv2-bg-white dark:ppv2-bg-gray-700 ppv2-text-gray-700 dark:ppv2-text-gray-300 ppv2-border ppv2-border-gray-200 dark:ppv2-border-gray-600 ppv2-rounded-lg poppinsMedium hover:ppv2-bg-gray-50 dark:hover:ppv2-bg-gray-600 ppv2-transition-colors ppv2-flex ppv2-items-center"
                                >
                                    {{ $t('website_settings.outils.visit_website') }}
                                    <ExternalLink :size="14" class="ppv2-ml-2" />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Separator (except last) -->
                <div
                    v-if="index < localOutils.length - 1"
                    class="ppv2-h-px ppv2-bg-gray-100 dark:ppv2-bg-gray-800 ppv2-my-6"
                ></div>
            </div>
        </div>

        <!-- Integration Modal -->
        <CustomDialog v-model="showModal" :defaultTitle="modalTitle" width="600px" @cancel="closeModal">
            <template #content>
                <div class="ppv2-mt-4">
                    <BaseTextarea
                        v-model="scriptValue"
                        :label="modalLabel"
                        :use-editor="false"
                        :rows="modalRows"
                        :placeholder="modalPlaceholder"
                        :required="true"
                        :error-text="errorMessage"
                    />
                </div>
            </template>
            <template #actions>
                <div class="ppv2-flex ppv2-gap-2">
                    <BaseButton variant="danger-outline" @click="clearScript">
                        {{ $t('website_settings.outils.modal.clear') }}
                    </BaseButton>
                    <BaseButton variant="primary" :loading="saving" @click="saveScript">
                        {{ $t('website_settings.outils.modal.add') }}
                    </BaseButton>
                </div>
            </template>
        </CustomDialog>
    </div>
</template>

<script>
import BaseTextarea from '../../../../Shared/BaseTextarea.vue'
import CustomDialog from '../../../../Shared/CustomDialog.vue'
import BaseButton from '../../../../Shared/BaseButton.vue'
import { Wrench, ExternalLink } from 'lucide-vue'
import normalizeSize from '../../../../../utils/normalizeSize'
import normalizeFontSize from '../../../../../utils/normalizeFontSize'

export default {
    name: 'TabOutils',
    components: {
        BaseTextarea,
        CustomDialog,
        BaseButton,
        Wrench,
        ExternalLink,
    },
    props: {
        data: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            localOutils: [],
            showModal: false,
            selectedOutil: null,
            scriptValue: '',
            errorMessage: '',
            saving: false,
        }
    },
    computed: {
        modalTitle() {
            if (!this.selectedOutil) return this.$t('website_settings.outils.modal.title_default')
            if (this.selectedOutil.name === 'Google Search') {
                return this.$t('website_settings.outils.modal.title_google_search')
            } else if (this.selectedOutil.name === 'Google Analytics') {
                return this.$t('website_settings.outils.modal.title_google_analytics')
            }
            return this.$t('website_settings.outils.modal.title_default')
        },
        modalLabel() {
            if (!this.selectedOutil) return this.$t('website_settings.outils.modal.label_default')
            if (this.selectedOutil.name === 'Google Search') {
                return this.$t('website_settings.outils.modal.label_google_search')
            } else if (this.selectedOutil.name === 'Google Analytics') {
                return this.$t('website_settings.outils.modal.label_google_analytics')
            }
            return this.$t('website_settings.outils.modal.label_default')
        },
        modalPlaceholder() {
            if (!this.selectedOutil) return this.$t('website_settings.outils.modal.placeholder_default')
            if (this.selectedOutil.name === 'Google Search') {
                return this.$t('website_settings.outils.modal.placeholder_google_search')
            } else if (this.selectedOutil.name === 'Google Analytics') {
                return this.$t('website_settings.outils.modal.placeholder_google_analytics')
            }
            return this.$t('website_settings.outils.modal.placeholder_default')
        },
        modalRows() {
            if (this.selectedOutil?.name === 'Google Analytics') {
                return 1
            }
            return 10
        },
    },
    mounted() {
        this.initializeForm()
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        initializeForm() {
            if (this.data?.outils) {
                this.localOutils = [...this.data.outils]
            }
        },
        async openIntegrationModal(outil) {
            this.selectedOutil = outil
            this.errorMessage = ''
            this.scriptValue = ''

            // Fetch current script value
            try {
                const response = await axios.post('/back-office/website/website-settings/fetch', {
                    id: outil.id,
                })
                this.scriptValue = response.data.outil?.script || ''
            } catch (error) {
                this.$toast.error(error.response.data.message)
            }

            this.showModal = true
        },
        closeModal() {
            this.showModal = false
            this.selectedOutil = null
            this.scriptValue = ''
            this.errorMessage = ''
        },
        async saveScript() {
            this.errorMessage = ''

            if (!this.scriptValue.trim()) {
                this.errorMessage = this.$t('website_settings.outils.modal.error_required')
                return
            }

            await this.updateScript()
        },
        async clearScript() {
            this.scriptValue = ''
            await this.updateScript()
        },
        async updateScript() {
            this.saving = true
            try {
                const response = await axios.post('/back-office/website/outils/updateScript', {
                    id: this.selectedOutil.id,
                    script: this.scriptValue,
                })

                this.$toast.success(response.data.message || this.$t('website_settings.outils.modal.update_success'))
                this.closeModal()
            } catch (error) {
                this.$toast.error(error.response.data.message)
            } finally {
                this.saving = false
            }
        },
    },
}
</script>

<style scoped>
.section-block {
    border-radius: 20px;
    padding: 24px;
    box-shadow:
        0 4px 6px -1px rgba(0, 0, 0, 0.1),
        0 2px 4px -1px rgba(0, 0, 0, 0.06);
    border: 1px solid rgba(0, 0, 0, 0.05);
}

.dark .section-block {
    border-color: rgba(255, 255, 255, 0.05);
}
</style>
